import { ChartDonut } from "@patternfly/react-charts";
import { IconCheckCircle } from "../../icons/IconCheckCircle/IconCheckCircle";
import "./LeafDonutWithIcon.scss";

export type LeafDonutWithIconProps = {
  achievedSegments?: number;
  totalSegments?: number;
  percentage?: number;
  isMet: boolean;
  size?: "sm" | "md" | "lg";
  variant: "legs" | "progress";
};

const SIZE_CONFIG = {
  sm: { chartSize: 32, innerRadius: 11, iconSize: "sm" as const },
  md: { chartSize: 36, innerRadius: 12, iconSize: "sm" as const },
  lg: { chartSize: 64, innerRadius: 24, iconSize: "lg" as const },
};

export const LeafDonutWithIcon = ({
  achievedSegments = 0,
  totalSegments = 0,
  percentage = 0,
  isMet,
  size = "lg",
  variant,
}: LeafDonutWithIconProps) => {
  const { chartSize, innerRadius, iconSize } = SIZE_CONFIG[size];

  const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

  const normalizedPercentage = isMet ? 100 : clamp(percentage, 0, 100);

  let data;
  let colorScale;

  if (variant === "legs") {
    data = Array.from({ length: totalSegments }, (_, i) => ({
      x: "",
      y: 1,
      isAchieved: i < achievedSegments,
    }));

    colorScale = data.map((d) => {
      if (!d.isAchieved) {
        return "var(--leaf-disabled)";
      }

      return isMet ? "var(--leaf-green)" : "var(--leaf-orange)";
    });
  } else {
    if (normalizedPercentage <= 0) {
      data = [{ x: "", y: 100 }];
      colorScale = ["var(--leaf-disabled)"];
    } else if (normalizedPercentage >= 100) {
      data = [{ x: "", y: 100 }];
      colorScale = ["var(--leaf-green)"];
    } else {
      data = [
        { x: "", y: normalizedPercentage },
        { x: "", y: 100 - normalizedPercentage },
      ];
      colorScale = ["var(--leaf-orange)", "var(--leaf-disabled)"];
    }
  }

  return (
    <div className={`leaf-donut leaf-donut--${size}`} style={{ width: chartSize, height: chartSize }}>
      <div className="leaf-donut__chart" aria-hidden="true">
        <ChartDonut
          data={data}
          width={chartSize}
          height={chartSize}
          innerRadius={innerRadius}
          padAngle={variant === "legs" ? 4 : 0}
          padding={0}
          labels={() => null}
          colorScale={colorScale}
        />
      </div>

      <div className="leaf-donut__icon">
        <IconCheckCircle size={iconSize} solidCheckCircle={isMet} color={isMet ? "green" : "mediumGray"} />
      </div>
    </div>
  );
};
